import * as React from "react";
import { useSettings } from "../../tools";
import { SipClient, useLogger, useSession } from "@dvrt/web-phone-client";
import { MeetingPageStage } from "conference/components/meeting-page/types";
import { useBoolState } from "@dvrt/react-tools";
import { getConferenceUri } from "conference/components/joinMeetingTools";
import { useDesktopShareAutoPin } from "../client/hooks";
import { Connecting } from "../widgets";

interface Props {
    client: SipClient;
    identifier: string;
    children: React.ReactNode;
}

const ConnectingToTequet = ({ sipClient }: { sipClient: SipClient }) => {
    const settings = useSettings();

    React.useEffect(() => {
        if (!sipClient.session) return;

        sipClient.session.on("failed", (_event) => {
            settings.stage = MeetingPageStage.Prepare;
        });
    }, [!sipClient.session]);

    return <Connecting />;
};

export function SessionStarter({ client, identifier, children }: Props) {
    const settings = useSettings();
    const [callConnected, setCallConnected] = useBoolState();
    const logger = useLogger("SessionStarter");

    const context = useSession();

    useDesktopShareAutoPin(context);

    React.useEffect(() => {
        if (client.connectionState === "connected") {
            logger.debug("starting call");
            context.manager.startCall(getConferenceUri(identifier), settings.video);
        }
    }, [client.connectionState]);

    React.useEffect(() => {
        if (!client.session) return;
        client.session.on("accepted", setCallConnected);
    }, [client.session]);

    React.useEffect(() => {
        logger.debug(`settings.audio changed ${settings.audio} ${context.manager.isStarted} ${callConnected}`);
        if (!context.manager.isStarted) return;
        context.manager.mute(!settings.audio);
    }, [settings.audio]);

    React.useEffect(() => {
        logger.debug(`settings.video changed ${settings.video}`);
        if (!context.manager.isStarted) return;
        if (context.manager.localVideoTrack && !settings.video) context.manager.removeVideo();

        if (!context.manager.localVideoTrack && settings.video) context.manager.addVideo();
    }, [settings.video]);

    return callConnected ? children : <ConnectingToTequet sipClient={client} />;
}
